import RefundCollectionSummary from '../components/RefundCollectionSummary';
import React, { useState } from 'react';
import { get } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
const fmt = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');

const REPORTS = [
  { key:'pnl',          label:'Profit & Loss',       icon:'📊' },
  { key:'sales',        label:'Sales Report',        icon:'🧾' },
  { key:'expenses',     label:'Expense Report',      icon:'💸' },
  { key:'inventory',    label:'Inventory Report',    icon:'📦' },
  { key:'payroll',      label:'Payroll Report',      icon:'💰' },
  { key:'job-performance', label:'Job Performance',  icon:'🧵' },
  { key:'customers',    label:'Customer Report',     icon:'👥' },
  { key:'suppliers',    label:'Supplier Report',     icon:'🏭' },
  { key:'production',   label:'Production Report',   icon:'🔨' },
  { key:'cashflow',     label:'Cashflow Report',     icon:'💳' },
  { key:'attendance',   label:'Attendance Summary',  icon:'🕐' },
  { key:'job-costing',  label:'Job Costing Report',  icon:'📋' },
  { key:'sales-performance', label:'Sales Performance', icon:'📈' },
  { key:'aging',             label:'Receivables Aging',  icon:'⏱' },
  { key:'customer-analysis', label:'Customer Analysis',  icon:'🔍' },
  { key:'adv-cashflow',      label:'Cash Flow Analysis', icon:'🏦' },
  { key:'supplier-spend',    label:'Supplier Spend',     icon:'🏭' },
  { key:'expense-breakdown', label:'Expense Breakdown',  icon:'🥧' },
  { key:'product-sales',     label:'Top Products',        icon:'🏆' },
];

export default function Reports() {
  const { user } = useAuth();
  const [activeReport, setActiveReport] = useState('pnl');
  const [from, setFrom] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  });
  const [to, setTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (user?.role !== 'super_admin') {
    return (
      <div className="empty-state">
        <h3>Access Restricted</h3>
        <p>Reports are only available to the Super Admin.</p>
      </div>
    );
  }

  const runReport = async () => {
    setLoading(true); setError(''); setData(null);
    try {
      const advancedKeys = ['sales-performance','aging','customer-analysis','adv-cashflow','supplier-spend'];
      const isAdvanced = advancedKeys.includes(activeReport);
      const apiKey = activeReport === 'adv-cashflow' ? 'cashflow' :
                     activeReport === 'customer-analysis' ? 'customers' : activeReport;
      if (['expense-breakdown','product-sales'].includes(activeReport) && !from) return;
      const baseUrl = isAdvanced ? '/advanced-reports/' : '/reports/';
      const qs = activeReport === 'inventory' ? '' :
                 activeReport === 'aging' ? '' : '?from='+from+'&to='+to;
      const result = await get(baseUrl+apiKey+qs);
      setData(result);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const printReport = () => {
    const reportEl = document.getElementById('report-output');
    if (!reportEl) return;
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html>
<html><head>
<meta charset="utf-8">
<title>${activeReport} Report — Bloans Creations Ltd.</title>
<style>
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family: Georgia, serif; color: #2C1F16; padding: 32px; background: #fff; }
  @media print {
    body { margin:0; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
    @page { margin: 1cm; size: A4; }
  }
  .report-header { border-bottom: 3px solid #556B2F; padding-bottom: 16px; margin-bottom: 24px; }
  .report-header h1 { font-size: 20px; color: #3B2A21; }
  .report-header p { font-size: 12px; color: #8A8A8A; margin-top: 4px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 16px; }
  th { background: #556B2F; color: #F2EFE7; padding: 8px 12px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: .05em; }
  td { padding: 8px 12px; border-bottom: 1px solid #E8E0D4; }
  tr:nth-child(even) td { background: #F9F8F5; }
  .tile { display: inline-block; background: #F2EFE7; border: 1px solid #E8E0D4; border-radius: 8px; padding: 12px 20px; margin: 6px; text-align: center; }
  .tile-val { font-size: 20px; font-weight: 700; color: #556B2F; }
  .tile-lbl { font-size: 11px; color: #8A8A8A; margin-top: 4px; }
  .section-title { font-size: 14px; font-weight: 700; color: #556B2F; text-transform: uppercase; letter-spacing: .06em; margin: 24px 0 12px; border-bottom: 1px solid #E8E0D4; padding-bottom: 6px; }
  .footer { margin-top: 32px; text-align: center; font-size: 10px; color: #8A8A8A; border-top: 1px solid #E8E0D4; padding-top: 12px; }
  .print-btn { display: block; margin: 16px auto; padding: 10px 24px; background: #556B2F; color: #fff; border: none; cursor: pointer; border-radius: 6px; font-size: 14px; }
  @media print { .print-btn { display: none; } }
</style>
</head><body>
  <div class="report-header">
    <h1>Bloans Creations Ltd. — ${activeReport} Report</h1>
    <p>Generated: ${new Date().toLocaleDateString('en-NG',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</p>
  </div>
  <button class="print-btn" onclick="window.print()">🖨️ Print / Save as PDF</button>
  ${reportEl.innerHTML}
  <div class="footer">Bloans Creations Ltd. · No. 3, S. Ade-Ojo Estate, Sawmill, Old Ife Road, Ibadan, Oyo State, Nigeria</div>
</body></html>`);
    w.document.close();
  };

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Reports</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>Business performance reports</p>
        </div>
        {data && <button className="btn btn-outline" onClick={printReport}>Print Report</button>}
      </div>

      {/* Report selector */}
      <div style={{display:'flex',gap:10,marginBottom:20,flexWrap:'wrap'}}>
        {REPORTS.map(r => (
          <button key={r.key}
            className={'btn '+(activeReport===r.key?'btn-primary':'btn-outline')}
            onClick={() => { setActiveReport(r.key); setData(null); }}>
            {r.icon} {r.label}
          </button>
        ))}
      </div>

      {/* Date range */}
      {activeReport !== 'inventory' && (
        <div style={{background:'#F2EFE7',borderRadius:8,padding:16,marginBottom:20,display:'flex',gap:16,alignItems:'flex-end',flexWrap:'wrap'}}>
          <div className="form-group" style={{margin:0}}>
            <label className="form-label">From Date</label>
            <input type="date" className="form-control" value={from} onChange={e => setFrom(e.target.value)} />
          </div>
          <div className="form-group" style={{margin:0}}>
            <label className="form-label">To Date</label>
            <input type="date" className="form-control" value={to} onChange={e => setTo(e.target.value)} />
          </div>
          <div style={{display:'flex',gap:8}}>
            {[
              {label:'This Month', fn:() => { const d=new Date(); setFrom(new Date(d.getFullYear(),d.getMonth(),1).toISOString().split('T')[0]); setTo(d.toISOString().split('T')[0]); }},
              {label:'Last Month', fn:() => { const d=new Date(); const f=new Date(d.getFullYear(),d.getMonth()-1,1); const t=new Date(d.getFullYear(),d.getMonth(),0); setFrom(f.toISOString().split('T')[0]); setTo(t.toISOString().split('T')[0]); }},
              {label:'This Year', fn:() => { const d=new Date(); setFrom(d.getFullYear()+'-01-01'); setTo(d.toISOString().split('T')[0]); }},
            ].map(p => (
              <button key={p.label} className="btn btn-ghost btn-sm" onClick={p.fn}>{p.label}</button>
            ))}
          </div>
          <button className="btn btn-primary" onClick={runReport} disabled={loading}>
            {loading ? 'Running...' : 'Run Report'}
          </button>
        </div>
      )}

      {activeReport === 'inventory' && (
        <button className="btn btn-primary mb-lg" onClick={runReport} disabled={loading}>
          {loading ? 'Loading...' : 'Run Inventory Report'}
        </button>
      )}

      {error && <div className="alert alert-error mb-md">{error}</div>}

      {/* Report output */}
      {data && (
        <div id="report-output">
          {activeReport === 'expense-breakdown' && <ExpenseBreakdownChart data={data} />}
          {activeReport === 'product-sales' && <ProductSalesChart data={data} />}
          {activeReport === 'pnl' && <PNLReport data={data} from={from} to={to} />}
          {activeReport === 'sales' && <SalesReport data={data} from={from} to={to} />}
          {activeReport === 'expenses' && <ExpenseReport data={data} from={from} to={to} />}
          {activeReport === 'inventory' && <InventoryReport data={data} />}
          {activeReport === 'payroll' && <PayrollReport data={data} from={from} to={to} />}
          {activeReport === 'job-performance' && <JobPerformanceReport data={data} from={from} to={to} />}
          {activeReport === 'customers' && <CustomerReport data={data} from={from} to={to} />}
          {activeReport === 'suppliers' && <SupplierReport data={data} from={from} to={to} />}
          {activeReport === 'production' && <ProductionReport data={data} from={from} to={to} />}
          {activeReport === 'cashflow' && <CashflowReport data={data} from={from} to={to} />}
          {activeReport === 'attendance' && <AttendanceReport data={data} from={from} to={to} />}
          {activeReport === 'job-costing' && <JobCostingReport data={data} from={from} to={to} />}
          {activeReport === 'sales-performance' && <SalesPerformanceReport data={data} from={from} to={to} />}
          {activeReport === 'aging' && <AgingReport data={data} />}
          {activeReport === 'customer-analysis' && <CustomerAnalysisReport data={data} from={from} to={to} />}
          {activeReport === 'adv-cashflow' && <CashFlowReport data={data} from={from} to={to} />}
          {activeReport === 'supplier-spend' && <SupplierSpendReport data={data} from={from} to={to} />}
        </div>
      )}
    </div>
  );
}

function ReportHeader({ title, from, to }) {
  return (
    <div style={{background:'#556B2F',padding:'16px 24px',borderRadius:'10px 10px 0 0',display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:0}}>
      <div>
        <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.3rem',fontWeight:600,color:'#A8893D'}}>{title}</div>
        <div style={{fontSize:11,color:'rgba(242,239,231,.5)',marginTop:2}}>Bloans Creations Ltd</div>
      </div>
      {from && to && (
        <div style={{textAlign:'right',fontSize:12,color:'rgba(242,239,231,.6)'}}>
          <div>Period: {from} to {to}</div>
          <div style={{marginTop:2}}>Generated: {new Date().toLocaleDateString('en-NG')}</div>
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, value, color }) {
  return (
    <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:8,padding:'14px 16px'}}>
      <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:6}}>{label}</div>
      <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.5rem',fontWeight:700,color:color||'#3B2A21'}}>{value}</div>
    </div>
  );
}

function PNLReport({ data, from, to }) {
  const { summary, expense_by_category, bill_by_supplier, monthly_trend } = data;
  return (
    <div style={{border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
      <ReportHeader title="Profit & Loss Report" from={from} to={to} />
      <div style={{padding:20}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:20}}>
          <SummaryTile label="Total Revenue" value={fmt(summary.total_revenue)} color="#556B2F" />
          <SummaryTile label="Total Expenses" value={fmt(summary.total_expenses)} color="#8F2E24" />
          <SummaryTile label="Net Profit" value={fmt(summary.gross_profit)} color={summary.gross_profit>=0?'#2E6B3A':'#8F2E24'} />
          <SummaryTile label="Outstanding" value={fmt(summary.outstanding_receivables)} color="#8B5E0A" />
        </div>

        {monthly_trend.length > 1 && (
          <div className="card mb-lg">
            <div className="card-hdr"><span className="card-title">Monthly Trend</span></div>
            <div style={{padding:16}}>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={monthly_trend}>
                  <XAxis dataKey="month" tick={{fontSize:11}} />
                  <YAxis tick={{fontSize:11}} tickFormatter={v=>'\u20a6'+(v/1000).toFixed(0)+'k'} />
                  <Tooltip formatter={v=>fmt(v)} />
                  <Legend />
                  <Bar dataKey="revenue" name="Revenue" fill="#556B2F" radius={[3,3,0,0]} />
                  <Bar dataKey="expenses" name="Expenses" fill="#A8893D" radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
          {expense_by_category.length > 0 && (
            <div>
              <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Expenses by Category</div>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                <thead><tr style={{background:'#F2EFE7'}}>
                  <th style={{padding:'8px 12px',textAlign:'left',fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Category</th>
                  <th style={{padding:'8px 12px',textAlign:'right',fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Amount</th>
                </tr></thead>
                <tbody>
                  {expense_by_category.map(e => (
                    <tr key={e.category}>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{e.category}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:14}}>{fmt(e.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {bill_by_supplier.length > 0 && (
            <div>
              <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Spend by Supplier</div>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                <thead><tr style={{background:'#F2EFE7'}}>
                  <th style={{padding:'8px 12px',textAlign:'left',fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Supplier</th>
                  <th style={{padding:'8px 12px',textAlign:'right',fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Paid</th>
                </tr></thead>
                <tbody>
                  {bill_by_supplier.map(b => (
                    <tr key={b.supplier}>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{b.supplier}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:14}}>{fmt(b.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SalesReport({ data, from, to }) {
  const { summary, top_customers, by_status, invoices } = data;
  return (
    <div style={{border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
      <ReportHeader title="Sales Report" from={from} to={to} />
      <div style={{padding:20}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:12,marginBottom:20}}>
          <SummaryTile label="Total Invoices" value={summary.invoice_count} />
          <SummaryTile label="Total Billed" value={fmt(summary.total_billed)} color="#556B2F" />
          <SummaryTile label="Gross Payments" value={fmt(summary.gross_collected)} color="#556B2F" />
          <SummaryTile label="Refunds" value={fmt(summary.refunds_paid)} color="#8F2E24" />
          <SummaryTile label="Net Payments Retained" value={fmt(summary.total_collected)} color="#2E6B3A" />
          <SummaryTile label="Outstanding" value={fmt(summary.total_outstanding)} color="#8F2E24" />
        </div>

        <p style={{fontSize:12,color:'#3B2A21'}}>Payments and refunds shown here belong to invoices dated within the selected period. Use Cash Flow for receipts and refunds by payment date.</p>
        {top_customers.length > 0 && (
          <div style={{marginBottom:20}}>
            <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Top Customers</div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead><tr style={{background:'#F2EFE7'}}>
                <th style={{padding:'8px 12px',textAlign:'left',fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Customer</th>
                <th style={{padding:'8px 12px',textAlign:'center',fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Invoices</th>
                <th style={{padding:'8px 12px',textAlign:'right',fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Billed</th>
                <th style={{padding:'8px 12px',textAlign:'right',fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Paid</th>
              </tr></thead>
              <tbody>
                {top_customers.map(c => (
                  <tr key={c.name}>
                    <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{c.name}</td>
                    <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'center'}}>{c.invoice_count}</td>
                    <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:14}}>{fmt(c.total_billed)}</td>
                    <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:'var(--paid)'}}>{fmt(c.total_paid)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>All Invoices</div>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>
            <th style={{padding:'7px 10px',textAlign:'left',fontSize:9,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Invoice #</th>
            <th style={{padding:'7px 10px',textAlign:'left',fontSize:9,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Date</th>
            <th style={{padding:'7px 10px',textAlign:'left',fontSize:9,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Customer</th>
            <th style={{padding:'7px 10px',textAlign:'right',fontSize:9,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Total</th>
            <th style={{padding:'7px 10px',textAlign:'right',fontSize:9,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Paid</th>
            <th style={{padding:'7px 10px',textAlign:'right',fontSize:9,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Balance</th>
            <th style={{padding:'7px 10px',fontSize:9,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Status</th>
          </tr></thead>
          <tbody>
            {invoices.map(i => (
              <tr key={i.id}>
                <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{i.invoice_number}</td>
                <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>{i.date}</td>
                <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>{i.customer_name}</td>
                <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:13}}>{fmt(i.total)}</td>
                <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:13,color:'var(--paid)'}}>{fmt(i.amount_paid)}</td>
                <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:13,color:parseFloat(i.balance_due)>0?'var(--unpaid)':'var(--paid)'}}>{fmt(i.balance_due)}</td>
                <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>
                  <span style={{fontSize:9,fontWeight:600,textTransform:'uppercase',padding:'2px 8px',borderRadius:20,
                    background:i.status==='paid'?'var(--paid-bg)':i.status==='part_paid'?'var(--part-bg)':'var(--unpaid-bg)',
                    color:i.status==='paid'?'var(--paid)':i.status==='part_paid'?'var(--part)':'var(--unpaid)'}}>
                    {i.status.replace('_',' ')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ExpenseReport({ data, from, to }) {
  const { bill_summary, expense_summary, by_category, bills, expenses, grand_total } = data;
  return (
    <div style={{border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
      <ReportHeader title="Expense Report" from={from} to={to} />
      <div style={{padding:20}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:20}}>
          <SummaryTile label="Bills Total" value={fmt(bill_summary.total_billed)} color="#8F2E24" />
          <SummaryTile label="Bills Paid" value={fmt(bill_summary.total_paid)} color="#556B2F" />
          <SummaryTile label="General Expenses" value={fmt(expense_summary.total)} color="#8F2E24" />
          <SummaryTile label="Grand Total" value={fmt(grand_total)} color="#8F2E24" />
        </div>

        {by_category.length > 0 && (
          <div style={{marginBottom:20}}>
            <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>By Category</div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead><tr style={{background:'#F2EFE7'}}>
                <th style={{padding:'8px 12px',textAlign:'left',fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Category</th>
                <th style={{padding:'8px 12px',textAlign:'right',fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Amount</th>
              </tr></thead>
              <tbody>
                {by_category.map(c => (
                  <tr key={c.category}>
                    <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{c.category}</td>
                    <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:14}}>{fmt(c.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {bills.length > 0 && (
          <div style={{marginBottom:20}}>
            <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Bills</div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead><tr style={{background:'#F2EFE7'}}>
                <th style={{padding:'7px 10px',textAlign:'left',fontSize:9,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Bill #</th>
                <th style={{padding:'7px 10px',textAlign:'left',fontSize:9,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Date</th>
                <th style={{padding:'7px 10px',textAlign:'left',fontSize:9,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Supplier</th>
                <th style={{padding:'7px 10px',textAlign:'right',fontSize:9,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Amount</th>
                <th style={{padding:'7px 10px',textAlign:'right',fontSize:9,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Paid</th>
                <th style={{padding:'7px 10px',fontSize:9,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Status</th>
              </tr></thead>
              <tbody>
                {bills.map(b => (
                  <tr key={b.id}>
                    <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{b.bill_number}</td>
                    <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>{b.date}</td>
                    <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>{b.supplier_name}</td>
                    <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:13}}>{fmt(b.amount)}</td>
                    <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:13,color:'var(--paid)'}}>{fmt(b.amount_paid)}</td>
                    <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>
                      <span className={'badge b-'+(b.status==='paid'?'paid':b.status==='part_paid'?'part':'unpaid')} style={{fontSize:9}}>{b.status.replace('_',' ')}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {expenses.length > 0 && (
          <div>
            <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>General Expenses</div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead><tr style={{background:'#F2EFE7'}}>
                <th style={{padding:'7px 10px',textAlign:'left',fontSize:9,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Date</th>
                <th style={{padding:'7px 10px',textAlign:'left',fontSize:9,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Category</th>
                <th style={{padding:'7px 10px',textAlign:'left',fontSize:9,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Description</th>
                <th style={{padding:'7px 10px',textAlign:'right',fontSize:9,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Amount</th>
              </tr></thead>
              <tbody>
                {expenses.map(e => (
                  <tr key={e.id}>
                    <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>{e.date}</td>
                    <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>{e.category}</td>
                    <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{e.description||'—'}</td>
                    <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:13}}>{fmt(e.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function InventoryReport({ data }) {
  const { materials, products, totals } = data;
  const grandTotal = parseFloat(totals.materials_value||0) + parseFloat(totals.products_value||0);
  return (
    <div style={{border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
      <ReportHeader title="Inventory Valuation Report" />
      <div style={{padding:20}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:20}}>
          <SummaryTile label="Materials Value" value={fmt(totals.materials_value)} color="#556B2F" />
          <SummaryTile label="Finished Goods Value" value={fmt(totals.products_value)} color="#556B2F" />
          <SummaryTile label="Total Inventory Value" value={fmt(grandTotal)} color="#3B2A21" />
        </div>

        <div style={{marginBottom:20}}>
          <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Raw Materials</div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{background:'#556B2F'}}>
              <th style={{padding:'8px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>Material</th>
              <th style={{padding:'8px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>Unit</th>
              <th style={{padding:'8px 12px',textAlign:'right',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>Stock</th>
              <th style={{padding:'8px 12px',textAlign:'right',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>Avg Cost</th>
              <th style={{padding:'8px 12px',textAlign:'right',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>Value</th>
              <th style={{padding:'8px 12px',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>Status</th>
            </tr></thead>
            <tbody>
              {materials.map((m,i) => (
                <tr key={m.name} style={{background:m.is_low_stock?'#FFFBF0':i%2===0?'#fff':'#F2EFE7'}}>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{m.name}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{m.unit_of_measure}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'right'}}>{Number(m.current_stock).toLocaleString()}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:14}}>{fmt(m.weighted_avg_cost)}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:14,fontWeight:600}}>{fmt(m.total_value)}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                    {m.is_low_stock && <span className="badge b-part" style={{fontSize:9}}>Low Stock</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Finished Goods</div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{background:'#556B2F'}}>
              <th style={{padding:'8px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>Product</th>
              <th style={{padding:'8px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>Size</th>
              <th style={{padding:'8px 12px',textAlign:'right',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>Stock</th>
              <th style={{padding:'8px 12px',textAlign:'right',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>Price</th>
              <th style={{padding:'8px 12px',textAlign:'right',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>Value</th>
              <th style={{padding:'8px 12px',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>Status</th>
            </tr></thead>
            <tbody>
              {products.map((p,i) => (
                <tr key={p.name} style={{background:p.is_low_stock?'#FFFBF0':i%2===0?'#fff':'#F2EFE7'}}>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{p.name}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{p.size||'—'}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'right'}}>{Number(p.current_stock).toLocaleString()}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:14}}>{fmt(p.selling_price)}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:14,fontWeight:600}}>{fmt(p.total_value)}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                    {p.is_low_stock && <span className="badge b-part" style={{fontSize:9}}>Low Stock</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── PAYROLL REPORT ────────────────────────────────────────────────────────────
function PayrollReport({ data, from, to }) {
  const t = data.totals || {};
  return (
    <div>
      <h3 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.4rem',marginBottom:16}}>
        Payroll Report — {from} to {to}
      </h3>
      <div className="metrics" style={{marginBottom:20}}>
        {[
          ['Total Gross', fmt(t.total_gross), false],
          ['Total Deductions', fmt(t.total_deductions), true],
          ['Total Net Paid', fmt(t.total_net), false],
          ['Payroll Runs', t.total_runs||0, false],
        ].map(([label,val,neg])=>(
          <div className="tile" key={label}>
            <div className="tile-lbl">{label}</div>
            <div className={`tile-val ${neg?'neg':''}`}>{val}</div>
          </div>
        ))}
      </div>
      <h4 style={{fontSize:13,fontWeight:600,color:'#556B2F',marginBottom:10}}>Payroll Runs</h4>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
        <thead>
          <tr style={{background:'#2C1F16'}}>
            {['Period','Staff','Gross','Deductions','Net','Status'].map(h=>(
              <th key={h} style={{padding:'8px 12px',textAlign:'left',color:'#E8C466',fontSize:9,textTransform:'uppercase'}}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(data.runs||[]).map((r,i)=>(
            <tr key={i} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{r.month}/{r.year}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{r.staff_count}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{fmt(r.total_gross)}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#8F2E24'}}>{fmt(r.total_deductions)}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:600}}>{fmt(r.total_net)}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                <span style={{fontSize:9,padding:'2px 8px',borderRadius:20,fontWeight:600,
                  background:r.status==='paid'?'#E8F5EB':'#FFF3DC',
                  color:r.status==='paid'?'#2E6B3A':'#8B5E0A',textTransform:'capitalize'}}>
                  {r.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── JOB PERFORMANCE REPORT ───────────────────────────────────────────────────
function JobPerformanceReport({ data, from, to }) {
  const s = data.summary || {};
  const onTimeRate = s.dispatched > 0 ? Math.round((s.on_time/s.dispatched)*100) : 0;
  return (
    <div>
      <h3 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.4rem',marginBottom:16}}>
        Job Performance — {from} to {to}
      </h3>
      <div className="metrics" style={{marginBottom:20}}>
        {[
          ['Total Jobs', s.total_jobs||0],
          ['Dispatched', s.dispatched||0],
          ['In Progress', s.in_progress||0],
          ['On Time', `${onTimeRate}%`],
          ['Late', s.late||0],
          ['Avg Days', s.avg_days_to_complete ? Math.round(s.avg_days_to_complete)+'d' : '—'],
        ].map(([label,val])=>(
          <div className="tile" key={label}>
            <div className="tile-lbl">{label}</div>
            <div className="tile-val">{val}</div>
          </div>
        ))}
      </div>
      <h4 style={{fontSize:13,fontWeight:600,color:'#556B2F',marginBottom:10}}>By Status</h4>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:20}}>
        <thead>
          <tr style={{background:'#2C1F16'}}>
            {['Status','Count','Total Qty'].map(h=>(
              <th key={h} style={{padding:'8px 12px',textAlign:'left',color:'#E8C466',fontSize:9,textTransform:'uppercase'}}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(data.by_status||[]).map((r,i)=>(
            <tr key={i} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',textTransform:'capitalize'}}>{r.status?.replace(/_/g,' ')}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{r.count}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{r.total_qty||'—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <h4 style={{fontSize:13,fontWeight:600,color:'#556B2F',marginBottom:10}}>Recent Jobs</h4>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
        <thead>
          <tr style={{background:'#2C1F16'}}>
            {['Job #','Customer','Description','Qty','Status','Created','Delivery'].map(h=>(
              <th key={h} style={{padding:'8px 12px',textAlign:'left',color:'#E8C466',fontSize:9,textTransform:'uppercase'}}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(data.jobs||[]).map((j,i)=>(
            <tr key={i} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:600,color:'#556B2F'}}>{j.job_number}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{j.customer_name||'—'}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{j.product_description}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{j.quantity}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',textTransform:'capitalize'}}>{j.status?.replace(/_/g,' ')}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{j.created_at?new Date(j.created_at).toLocaleDateString('en-NG'):'—'}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{j.delivery_date?new Date(j.delivery_date).toLocaleDateString('en-NG'):'—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── CUSTOMER REPORT ───────────────────────────────────────────────────────────
function CustomerReport({ data, from, to }) {
  const s = data.summary || {};
  return (
    <div>
      <h3 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.4rem',marginBottom:16}}>
        Customer Report — {from} to {to}
      </h3>
      <div className="metrics" style={{marginBottom:20}}>
        {[
          ['Total Customers', s.total_customers||0],
          ['Total Billed', fmt(s.total_billed)],
          ['Total Paid', fmt(s.total_paid)],
          ['Outstanding', fmt(s.total_outstanding), true],
        ].map(([label,val,neg])=>(
          <div className="tile" key={label}>
            <div className="tile-lbl">{label}</div>
            <div className={`tile-val ${neg?'neg':''}`}>{val}</div>
          </div>
        ))}
      </div>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
        <thead>
          <tr style={{background:'#2C1F16'}}>
            {['Customer','Invoices','Total Billed','Total Paid','Outstanding','Last Invoice'].map(h=>(
              <th key={h} style={{padding:'8px 12px',textAlign:'left',color:'#E8C466',fontSize:9,textTransform:'uppercase'}}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(data.customers||[]).map((c,i)=>(
            <tr key={i} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:600}}>{c.name}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{c.invoice_count}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{fmt(c.total_billed)}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#2E6B3A'}}>{fmt(c.total_paid)}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:parseFloat(c.total_outstanding)>0?'#8F2E24':'#2E6B3A',fontWeight:600}}>{fmt(c.total_outstanding)}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{c.last_invoice?new Date(c.last_invoice).toLocaleDateString('en-NG'):'—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── SUPPLIER REPORT ───────────────────────────────────────────────────────────
function SupplierReport({ data, from, to }) {
  const s = data.summary || {};
  return (
    <div>
      <h3 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.4rem',marginBottom:16}}>
        Supplier Report — {from} to {to}
      </h3>
      <div className="metrics" style={{marginBottom:20}}>
        {[
          ['Total Suppliers', s.total_suppliers||0],
          ['Total Billed', fmt(s.total_billed)],
          ['Total Paid', fmt(s.total_paid)],
          ['Outstanding', fmt(s.total_outstanding), true],
        ].map(([label,val,neg])=>(
          <div className="tile" key={label}>
            <div className="tile-lbl">{label}</div>
            <div className={`tile-val ${neg?'neg':''}`}>{val}</div>
          </div>
        ))}
      </div>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
        <thead>
          <tr style={{background:'#2C1F16'}}>
            {['Supplier','Bills','Total Billed','Total Paid','Outstanding','Last Bill'].map(h=>(
              <th key={h} style={{padding:'8px 12px',textAlign:'left',color:'#E8C466',fontSize:9,textTransform:'uppercase'}}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(data.suppliers||[]).map((s,i)=>(
            <tr key={i} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:600}}>{s.name}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{s.bill_count}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{fmt(s.total_billed)}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#2E6B3A'}}>{fmt(s.total_paid)}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:parseFloat(s.total_outstanding)>0?'#8F2E24':'#2E6B3A',fontWeight:600}}>{fmt(s.total_outstanding)}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{s.last_bill?new Date(s.last_bill).toLocaleDateString('en-NG'):'—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── PRODUCTION REPORT ─────────────────────────────────────────────────────────
function ProductionReport({ data, from, to }) {
  const s = data.summary || {};
  return (
    <div>
      <h3 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.4rem',marginBottom:16}}>
        Production Report — {from} to {to}
      </h3>
      <div className="metrics" style={{marginBottom:20}}>
        {[
          ['Total Produced', s.total_produced||0],
          ['Total Rework', s.total_rework||0],
          ['Count Entries', s.total_entries||0],
          ['Jobs Tracked', s.jobs_tracked||0],
        ].map(([label,val])=>(
          <div className="tile" key={label}>
            <div className="tile-lbl">{label}</div>
            <div className="tile-val">{val}</div>
          </div>
        ))}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
        <div>
          <h4 style={{fontSize:13,fontWeight:600,color:'#556B2F',marginBottom:10}}>By Stage</h4>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead>
              <tr style={{background:'#2C1F16'}}>
                {['Stage','Qty','Rework','Entries'].map(h=>(
                  <th key={h} style={{padding:'8px 12px',textAlign:'left',color:'#E8C466',fontSize:9,textTransform:'uppercase'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.by_stage||[]).map((r,i)=>(
                <tr key={i} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',textTransform:'capitalize'}}>{r.production_stage?.replace(/_/g,' ')}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:600}}>{r.total_qty}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#8F2E24'}}>{r.total_rework||0}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{r.entries}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <h4 style={{fontSize:13,fontWeight:600,color:'#556B2F',marginBottom:10}}>By Staff</h4>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead>
              <tr style={{background:'#2C1F16'}}>
                {['Staff','Total Qty','Rework'].map(h=>(
                  <th key={h} style={{padding:'8px 12px',textAlign:'left',color:'#E8C466',fontSize:9,textTransform:'uppercase'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.by_staff||[]).map((r,i)=>(
                <tr key={i} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:600}}>{r.counted_by_name||'—'}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{r.total_qty}</td>
                  <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#8F2E24'}}>{r.total_rework||0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── CASHFLOW REPORT ───────────────────────────────────────────────────────────
function CashflowReport({ data, from, to }) {
  const s = data.summary || {};
  return (
    <div>
      <h3 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.4rem',marginBottom:16}}>
        Cashflow Report — {from} to {to}
      </h3>
      <RefundCollectionSummary collections={data.customer_collections} unallocated={data.unallocated_refunds} />
      <div className="metrics" style={{marginBottom:20}}>
        {[
          ['Total In', fmt(s.total_in)],
          ['Total Out', fmt(s.total_out), true],
          ['Refunds (included in Total Out)', fmt(s.refunds_out), true],
          ['Net', fmt(s.net), parseFloat(s.net||0)<0],
        ].map(([label,val,neg])=>(
          <div className="tile" key={label}>
            <div className="tile-lbl">{label}</div>
            <div className={`tile-val ${neg?'neg':''}`}>{val}</div>
          </div>
        ))}
      </div>
      <h4 style={{fontSize:13,fontWeight:600,color:'#556B2F',marginBottom:10}}>By Bank Account</h4>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:20}}>
        <thead>
          <tr style={{background:'#2C1F16'}}>
            {['Account','Bank','In','Out','Current Balance'].map(h=>(
              <th key={h} style={{padding:'8px 12px',textAlign:'left',color:'#E8C466',fontSize:9,textTransform:'uppercase'}}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(data.by_account||[]).map((a,i)=>(
            <tr key={i} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:600}}>{a.account_name}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{a.bank_name||'—'}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#2E6B3A'}}>{fmt(a.total_in)}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#8F2E24'}}>{fmt(a.total_out)}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:600}}>{fmt(a.current_balance)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── ATTENDANCE SUMMARY REPORT ─────────────────────────────────────────────────
function AttendanceReport({ data, from, to }) {
  const s = data.summary || {};
  return (
    <div>
      <h3 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.4rem',marginBottom:16}}>
        Attendance Summary — {from} to {to}
      </h3>
      <div className="metrics" style={{marginBottom:20}}>
        {[
          ['Staff', s.staff_count||0],
          ['Days Attended', s.total_days_attended||0],
          ['Days Absent', s.total_days_absent||0],
          ['Total Lates', s.total_lates||0],
          ['Total Penalties', fmt(s.total_penalties), true],
        ].map(([label,val,neg])=>(
          <div className="tile" key={label}>
            <div className="tile-lbl">{label}</div>
            <div className={`tile-val ${neg?'neg':''}`}>{val}</div>
          </div>
        ))}
      </div>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
        <thead>
          <tr style={{background:'#2C1F16'}}>
            {['Staff','Days Req','Attended','Absent','Lates','Late Mins','Penalties'].map(h=>(
              <th key={h} style={{padding:'8px 12px',textAlign:'left',color:'#E8C466',fontSize:9,textTransform:'uppercase'}}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(data.by_staff||[]).map((r,i)=>(
            <tr key={i} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:600}}>{r.name||'—'}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{r.days_required||0}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#2E6B3A'}}>{r.days_attended||0}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:r.days_absent>0?'#8F2E24':'#2E6B3A'}}>{r.days_absent||0}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{r.late_count||0}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{r.late_minutes||0}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#8F2E24',fontWeight:600}}>
                {fmt(parseFloat(r.lateness_penalty||0)+parseFloat(r.early_signout_penalty||0)+parseFloat(r.absence_penalty||0))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── JOB COSTING REPORT ────────────────────────────────────────────────────────
function JobCostingReport({ data, from, to }) {
  const s = data.summary || {};
  return (
    <div>
      <h3 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.4rem',marginBottom:16}}>
        Job Costing Report — {from} to {to}
      </h3>
      <div className="metrics" style={{marginBottom:20}}>
        {[
          ['Total Jobs', s.total_jobs||0],
          ['Total Revenue', fmt(s.total_revenue)],
          ['Total Costs', fmt(s.total_costs), true],
          ['Gross Margin', fmt(s.total_margin), parseFloat(s.total_margin||0)<0],
        ].map(([label,val,neg])=>(
          <div className="tile" key={label}>
            <div className="tile-lbl">{label}</div>
            <div className={`tile-val ${neg?'neg':''}`}>{val}</div>
          </div>
        ))}
      </div>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
        <thead>
          <tr style={{background:'#2C1F16'}}>
            {['Job #','Customer','Description','Qty','Revenue','Costs','Margin','Margin %'].map(h=>(
              <th key={h} style={{padding:'8px 12px',textAlign:'left',color:'#E8C466',fontSize:9,textTransform:'uppercase'}}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(data.jobs||[]).map((j,i)=>(
            <tr key={i} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:600,color:'#556B2F'}}>{j.job_number}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{j.customer_name||'—'}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{j.product_description}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{j.quantity}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{fmt(j.invoice_amount)}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#8F2E24'}}>{fmt(j.total_costs)}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:600,
                color:parseFloat(j.gross_margin||0)>=0?'#2E6B3A':'#8F2E24'}}>{fmt(j.gross_margin)}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{j.margin_pct}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ── FROM ADVANCED REPORTS ────────────────────────────────────────────────────
function SalesPerformanceReport({ data, from, to }) {
  const { by_rep, monthly_trend, top_customers } = data;
  const totalRevenue = by_rep.reduce((s,r)=>s+parseFloat(r.total_collected||0),0);

  // Build chart data - one bar per month, grouped by rep
  const months = [...new Set(monthly_trend.map(r=>r.month))];
  const reps = [...new Set(monthly_trend.map(r=>r.rep_name))];
  const chartData = months.map(month => {
    const obj = { month };
    reps.forEach(rep => {
      const found = monthly_trend.find(r=>r.month===month && r.rep_name===rep);
      obj[rep] = found ? parseFloat(found.collected) : 0;
    });
    return obj;
  });

  const COLORS = ['#556B2F','#A8893D','#1E4FAD','#8F2E24','#2E6B3A'];

  return (
    <div style={{border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
      <ReportHeader title="Sales Performance Report" from={from} to={to} />
      <div style={{padding:20}}>
        {/* Rep summary cards */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:12,marginBottom:20}}>
          {by_rep.map((rep,i) => (
            <div key={rep.rep_name} style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden',boxShadow:'var(--shadow)'}}>
              <div style={{background:COLORS[i%COLORS.length],padding:'10px 14px'}}>
                <div style={{fontWeight:600,fontSize:13,color:'#F2EFE7'}}>{rep.rep_name}</div>
                <div style={{fontSize:10,color:'rgba(242,239,231,.5)',textTransform:'capitalize'}}>{rep.role?.replace('_',' ')}</div>
              </div>
              <div style={{padding:'12px 14px'}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:6,fontSize:12}}>
                  <span style={{color:'#8A8A8A'}}>Invoices</span>
                  <span style={{fontWeight:600}}>{rep.invoice_count}</span>
                </div>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:6,fontSize:12}}>
                  <span style={{color:'#8A8A8A'}}>Billed</span>
                  <span>{fmt(rep.total_billed)}</span>
                </div>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:6,fontSize:12}}>
                  <span style={{color:'#8A8A8A'}}>Collected</span>
                  <span style={{color:'var(--paid)',fontWeight:600}}>{fmt(rep.total_collected)}</span>
                </div>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:12}}>
                  <span style={{color:'#8A8A8A'}}>Collection Rate</span>
                  <span style={{color:parseFloat(rep.collection_rate)>=80?'var(--paid)':'var(--part)',fontWeight:600}}>{rep.collection_rate}%</span>
                </div>
                <div style={{marginTop:8,height:4,background:'#F2EFE7',borderRadius:2}}>
                  <div style={{height:'100%',borderRadius:2,background:COLORS[i%COLORS.length],width:Math.min(100,parseFloat(rep.collection_rate)||0)+'%'}}></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Total */}
        <div style={{background:'#F2EFE7',borderRadius:8,padding:'14px 18px',marginBottom:20,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <span style={{fontWeight:600,fontSize:14,color:'#3B2A21'}}>Total Revenue Collected</span>
          <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.6rem',fontWeight:700,color:'#556B2F'}}>{fmt(totalRevenue)}</span>
        </div>

        {/* Monthly trend chart */}
        {chartData.length > 1 && (
          <div className="card" style={{marginBottom:20}}>
            <div className="card-hdr"><span className="card-title">Monthly Performance by Rep</span></div>
            <div style={{padding:16}}>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData}>
                  <XAxis dataKey="month" tick={{fontSize:11}} />
                  <YAxis tick={{fontSize:11}} tickFormatter={v=>'\u20a6'+(v/1000).toFixed(0)+'k'} />
                  <Tooltip formatter={v=>fmt(v)} />
                  <Legend />
                  {reps.map((rep,i) => (
                    <Bar key={rep} dataKey={rep} fill={COLORS[i%COLORS.length]} radius={[3,3,0,0]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Top customers */}
        <div>
          <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Top Customers</div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{background:'#556B2F'}}>
              {['Customer','Sales Rep','Orders','Total Billed','Total Paid'].map(h=>(
                <th key={h} style={{padding:'9px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {top_customers.map((c,i) => (
                <tr key={i} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{c.customer}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{c.rep_name}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'center'}}>{c.orders}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14}}>{fmt(c.total_billed)}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:'var(--paid)',fontWeight:600}}>{fmt(c.total_paid)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AgingReport({ data }) {
  const { invoices, summary, by_customer } = data;
  const buckets = [
    { label:'0-30 Days', value:parseFloat(summary.bucket_0_30), color:'#2E6B3A', bg:'#E8F5EB' },
    { label:'31-60 Days', value:parseFloat(summary.bucket_31_60), color:'#8B5E0A', bg:'#FFF3DC' },
    { label:'61-90 Days', value:parseFloat(summary.bucket_61_90), color:'#8F2E24', bg:'#FDEAEA' },
    { label:'90+ Days', value:parseFloat(summary.bucket_90_plus), color:'#5C0A0A', bg:'#FFE4E4' },
  ];

  return (
    <div style={{border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
      <ReportHeader title="Receivables Aging Report" />
      <div style={{padding:20}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12,marginBottom:20}}>
          <SummaryTile label="Total Outstanding" value={fmt(summary.total_outstanding)} color="#8F2E24" />
          {buckets.map(b => (
            <div key={b.label} style={{background:b.bg,borderRadius:8,padding:'12px 14px',border:'1px solid '+b.color+'33'}}>
              <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:b.color,marginBottom:6}}>{b.label}</div>
              <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.3rem',fontWeight:700,color:b.color}}>{fmt(b.value)}</div>
            </div>
          ))}
        </div>

        {/* By customer */}
        <div style={{marginBottom:20}}>
          <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Top Customers by Amount Owing</div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{background:'#556B2F'}}>
              {['Customer','Phone','Invoices','Total Owing','Oldest Invoice'].map(h=>(
                <th key={h} style={{padding:'9px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {by_customer.map((c,i) => (
                <tr key={i} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{c.customer}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{c.phone||'—'}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'center'}}>{c.invoice_count}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:'#8F2E24',fontWeight:600}}>{fmt(c.total_owing)}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',color:parseInt(c.oldest_invoice_days)>90?'#8F2E24':parseInt(c.oldest_invoice_days)>60?'#8B5E0A':'#555',fontWeight:500}}>{c.oldest_invoice_days} days</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* All invoices */}
        <div>
          <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>All Outstanding Invoices</div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead><tr style={{background:'#F2EFE7'}}>
              {['Invoice','Date','Customer','Total','Paid','Balance','Age','Bucket'].map(h=>(
                <th key={h} style={{padding:'8px 10px',textAlign:'left',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {invoices.map((inv,i) => (
                <tr key={i} style={{background:inv.aging_bucket==='90+ days'?'#FFF0F0':i%2===0?'#fff':'#F9F9F9'}}>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{inv.invoice_number}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>{inv.date}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>{inv.customer_name}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:13}}>{fmt(inv.total)}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:13,color:'var(--paid)'}}>{fmt(inv.amount_paid)}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:13,color:'#8F2E24',fontWeight:600}}>{fmt(inv.balance_due)}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>{inv.days_outstanding} days</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>
                    <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                      background:inv.aging_bucket==='0-30 days'?'#E8F5EB':inv.aging_bucket==='31-60 days'?'#FFF3DC':inv.aging_bucket==='61-90 days'?'#FDEAEA':'#FFE4E4',
                      color:inv.aging_bucket==='0-30 days'?'#2E6B3A':inv.aging_bucket==='31-60 days'?'#8B5E0A':inv.aging_bucket==='61-90 days'?'#8F2E24':'#5C0A0A'}}>
                      {inv.aging_bucket}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CustomerAnalysisReport({ data, from, to }) {
  const { lifetime_value, new_vs_returning, at_risk } = data;
  return (
    <div style={{border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
      <ReportHeader title="Customer Analysis Report" from={from} to={to} />
      <div style={{padding:20}}>
        {/* Summary */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:20}}>
          <SummaryTile label="New Customers" value={new_vs_returning?.new_customers||0} color="#1E4FAD" />
          <SummaryTile label="Returning Customers" value={new_vs_returning?.returning_customers||0} color="#556B2F" />
          <SummaryTile label="Avg Lifetime Value" value={fmt(new_vs_returning?.avg_lifetime_value||0)} color="#A8893D" />
        </div>

        {/* At Risk */}
        {at_risk.length > 0 && (
          <div style={{marginBottom:20}}>
            <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8F2E24',marginBottom:10}}>⚠ At-Risk Customers (No Order in 60+ Days)</div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead><tr style={{background:'#8F2E24'}}>
                {['Customer','Phone','Pipeline Stage','Last Order','Days Inactive','Total Orders','Lifetime Value'].map(h=>(
                  <th key={h} style={{padding:'9px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {at_risk.map((c,i) => (
                  <tr key={i} style={{background:i%2===0?'#fff':'#FFF8F8'}}>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{c.name}</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{c.phone||'—'}</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',textTransform:'capitalize'}}>{(c.pipeline_stage||'').replace('_',' ')}</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8'}}>{c.last_order||'Never'}</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',color:'#8F2E24',fontWeight:600}}>{c.days_inactive||'—'} days</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'center'}}>{c.total_orders}</td>
                    <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:'#556B2F'}}>{fmt(c.lifetime_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Lifetime value table */}
        <div>
          <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Customer Lifetime Value</div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead><tr style={{background:'#556B2F'}}>
              {['Customer','Phone','Orders','Lifetime Value','Total Paid','First Order','Last Order','Days Since'].map(h=>(
                <th key={h} style={{padding:'8px 10px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {lifetime_value.map((c,i) => (
                <tr key={i} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{c.name}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{c.phone||'—'}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',textAlign:'center'}}>{c.total_orders}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:13,color:'#556B2F',fontWeight:600}}>{fmt(c.lifetime_value)}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:13,color:'var(--paid)'}}>{fmt(c.total_paid)}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>{c.first_order||'—'}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>{c.last_order||'—'}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',color:parseInt(c.days_since_last_order)>90?'#8F2E24':parseInt(c.days_since_last_order)>60?'#8B5E0A':'inherit'}}>{c.days_since_last_order||'—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CashFlowReport({ data, from, to }) {
  const { monthly, bank_balances, outstanding } = data;
  const totalIn = monthly.reduce((s,m)=>s+m.money_in,0);
  const totalOut = monthly.reduce((s,m)=>s+m.money_out,0);
  const totalBankBalance = bank_balances.reduce((s,b)=>s+parseFloat(b.balance||0),0);

  return (
    <div style={{border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
      <ReportHeader title="Cash Flow Report" from={from} to={to} />
      <div style={{padding:20}}>
        <RefundCollectionSummary collections={data.customer_collections} unallocated={data.unallocated_refunds} />
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:12,marginBottom:20}}>
          <SummaryTile label="Total Money In" value={fmt(totalIn)} color="#556B2F" />
          <SummaryTile label="Total Money Out" value={fmt(totalOut)} color="#8F2E24" />
          <SummaryTile label="Refunds (included in money out)" value={fmt(monthly.reduce((sum,m)=>sum+Number(m.refunds_out||0),0))} color="#8F2E24" />
          <SummaryTile label="Net Cash Flow" value={fmt(totalIn-totalOut)} color={totalIn>=totalOut?'#2E6B3A':'#8F2E24'} />
          <SummaryTile label="Total Bank Balance" value={fmt(totalBankBalance)} color="#1E4FAD" />
        </div>

        {/* Outstanding */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:20}}>
          <div style={{background:'#E8F5EB',borderRadius:8,padding:'14px 16px',border:'1px solid #B8DEC0'}}>
            <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#2E6B3A',marginBottom:6}}>Outstanding Receivables (Money Coming In)</div>
            <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.5rem',fontWeight:700,color:'#2E6B3A'}}>{fmt(outstanding?.receivables)}</div>
          </div>
          <div style={{background:'#FDEAEA',borderRadius:8,padding:'14px 16px',border:'1px solid #F5B8B8'}}>
            <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8F2E24',marginBottom:6}}>Outstanding Payables (Money Going Out)</div>
            <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.5rem',fontWeight:700,color:'#8F2E24'}}>{fmt(outstanding?.payables)}</div>
          </div>
        </div>

        {/* Chart */}
        {monthly.length > 0 && (
          <div className="card" style={{marginBottom:20}}>
            <div className="card-hdr"><span className="card-title">Monthly Cash Flow</span></div>
            <div style={{padding:16}}>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={monthly}>
                  <XAxis dataKey="month" tick={{fontSize:11}} />
                  <YAxis tick={{fontSize:11}} tickFormatter={v=>'\u20a6'+(v/1000).toFixed(0)+'k'} />
                  <Tooltip formatter={v=>fmt(v)} />
                  <Legend />
                  <Bar dataKey="money_in" name="Money In" fill="#556B2F" radius={[3,3,0,0]} />
                  <Bar dataKey="money_out" name="Money Out" fill="#8F2E24" radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Bank balances */}
        <div style={{marginBottom:20}}>
          <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Bank Account Balances</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:12}}>
            {bank_balances.map(b => (
              <div key={b.name} style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:8,padding:'12px 14px'}}>
                <div style={{fontWeight:600,fontSize:13,color:'#3B2A21',marginBottom:2}}>{b.name}</div>
                <div style={{fontSize:11,color:'#8A8A8A',marginBottom:8}}>{b.bank_name} | {b.account_number}</div>
                <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.3rem',fontWeight:700,color:parseFloat(b.balance)>=0?'#556B2F':'#8F2E24'}}>{fmt(b.balance)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Monthly breakdown table */}
        <div>
          <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Monthly Breakdown</div>
          <div style={{overflowX:'auto'}}><table style={{width:'100%',minWidth:980,borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{background:'#556B2F'}}>
              {['Month','Money In','Bills Paid','Expenses','Refunds','Other Outflows','Total Out','Net'].map(h=>(
                <th key={h} style={{padding:'9px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {monthly.map((m,i) => (
                <tr key={i} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{m.month}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:'var(--paid)'}}>{fmt(m.money_in)}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:'#8F2E24'}}>{fmt(m.bills_out)}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:'#8F2E24'}}>{fmt(m.expenses_out)}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',color:'#8F2E24'}}>{fmt(m.refunds_out)}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',color:'#8F2E24'}}>{fmt(m.other_out)}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:'#8F2E24',fontWeight:600}}>{fmt(m.money_out)}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,fontWeight:700,color:m.net>=0?'#556B2F':'#8F2E24'}}>{fmt(m.net)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      </div>
    </div>
  );
}

function SupplierSpendReport({ data, from, to }) {
  const { by_supplier, monthly_trend } = data;
  const totalSpent = by_supplier.reduce((s,r)=>s+parseFloat(r.total_paid||0),0);
  const totalOwing = by_supplier.reduce((s,r)=>s+parseFloat(r.total_owing||0),0);

  return (
    <div style={{border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
      <ReportHeader title="Supplier Spend Analysis" from={from} to={to} />
      <div style={{padding:20}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:20}}>
          <SummaryTile label="Total Suppliers" value={by_supplier.length} />
          <SummaryTile label="Total Spent" value={fmt(totalSpent)} color="#8F2E24" />
          <SummaryTile label="Total Owing" value={fmt(totalOwing)} color="#8B5E0A" />
        </div>

        <div style={{marginBottom:20}}>
          <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Spend by Supplier</div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{background:'#556B2F'}}>
              {['Supplier','Category','Bills','Total Billed','Total Paid','Owing'].map(h=>(
                <th key={h} style={{padding:'9px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {by_supplier.map((s,i) => (
                <tr key={i} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{s.name}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{s.category||'—'}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'center'}}>{s.bill_count}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14}}>{fmt(s.total_billed)}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:'#8F2E24',fontWeight:600}}>{fmt(s.total_paid)}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,color:parseFloat(s.total_owing)>0?'#8B5E0A':'var(--paid)'}}>{fmt(s.total_owing)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ExpenseBreakdownChart({ data }) {
  const fmt = n => '₦' + Number(n||0).toLocaleString('en-NG');
  const items = data.data || [];
  const max = Math.max(...items.map(i => parseFloat(i.total)));
  const colors = ['#556B2F','#A8893D','#8F2E24','#3B5BDB','#8B5E0A','#2E6B3A','#6B3FA0','#C2255C','#1864AB','#e67700'];
  return (
    <div>
      <h3 style={{fontFamily:'Georgia,serif',color:'#3B2A21',marginBottom:20}}>Expense Breakdown by Category</h3>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:24}}>
        {/* Bar chart */}
        <div>
          {items.map((item,i) => (
            <div key={item.category} style={{marginBottom:12}}>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:12,marginBottom:4}}>
                <span style={{fontWeight:600,color:'#3B2A21'}}>{item.category}</span>
                <span style={{color:colors[i%colors.length],fontWeight:700}}>{fmt(item.total)}</span>
              </div>
              <div style={{background:'#F2EFE7',borderRadius:4,height:24,overflow:'hidden'}}>
                <div style={{
                  width: (parseFloat(item.total)/max*100)+'%',
                  height:'100%',
                  background:colors[i%colors.length],
                  borderRadius:4,
                  display:'flex',alignItems:'center',paddingLeft:8,
                  fontSize:10,color:'#fff',fontWeight:600,minWidth:30
                }}>{item.count} items</div>
              </div>
            </div>
          ))}
        </div>
        {/* Summary table */}
        <div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead><tr style={{background:'#556B2F'}}>
              <th style={{padding:'8px 12px',color:'#F2EFE7',textAlign:'left'}}>Category</th>
              <th style={{padding:'8px 12px',color:'#F2EFE7',textAlign:'right'}}>Count</th>
              <th style={{padding:'8px 12px',color:'#F2EFE7',textAlign:'right'}}>Total</th>
            </tr></thead>
            <tbody>
              {items.map((item,i)=>(
                <tr key={item.category} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
                  <td style={{padding:'8px 12px',fontWeight:500}}>{item.category}</td>
                  <td style={{padding:'8px 12px',textAlign:'right',color:'#8A8A8A'}}>{item.count}</td>
                  <td style={{padding:'8px 12px',textAlign:'right',fontWeight:700,color:'#556B2F'}}>{fmt(item.total)}</td>
                </tr>
              ))}
              <tr style={{background:'#F2EFE7',fontWeight:700}}>
                <td style={{padding:'8px 12px'}}>TOTAL</td>
                <td style={{padding:'8px 12px',textAlign:'right'}}>{items.reduce((s,i)=>s+parseInt(i.count),0)}</td>
                <td style={{padding:'8px 12px',textAlign:'right',color:'#556B2F'}}>{fmt(items.reduce((s,i)=>s+parseFloat(i.total),0))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ProductSalesChart({ data }) {
  const fmt = n => '₦' + Number(n||0).toLocaleString('en-NG');
  const items = data.data || [];
  const maxQty = Math.max(...items.map(i => parseFloat(i.total_qty)));
  const colors = ['#A8893D','#556B2F','#8F2E24','#3B5BDB','#8B5E0A','#2E6B3A','#6B3FA0','#C2255C','#1864AB','#e67700'];
  return (
    <div>
      <h3 style={{fontFamily:'Georgia,serif',color:'#3B2A21',marginBottom:20}}>Top Products by Sales</h3>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:24}}>
        {/* Bar chart by quantity */}
        <div>
          <div style={{fontSize:11,fontWeight:700,color:'#8A8A8A',textTransform:'uppercase',marginBottom:8}}>By Units Sold</div>
          {items.map((item,i) => (
            <div key={i} style={{marginBottom:12}}>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:12,marginBottom:4}}>
                <span style={{fontWeight:600,color:'#3B2A21',maxWidth:'70%',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{item.product_name}</span>
                <span style={{color:colors[i%colors.length],fontWeight:700}}>{Number(item.total_qty).toLocaleString()} units</span>
              </div>
              <div style={{background:'#F2EFE7',borderRadius:4,height:24,overflow:'hidden'}}>
                <div style={{
                  width:(parseFloat(item.total_qty)/maxQty*100)+'%',
                  height:'100%',background:colors[i%colors.length],borderRadius:4,
                  display:'flex',alignItems:'center',paddingLeft:8,
                  fontSize:10,color:'#fff',fontWeight:600,minWidth:30
                }}>{item.order_count} orders</div>
              </div>
            </div>
          ))}
        </div>
        {/* Table by revenue */}
        <div>
          <div style={{fontSize:11,fontWeight:700,color:'#8A8A8A',textTransform:'uppercase',marginBottom:8}}>By Revenue</div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead><tr style={{background:'#A8893D'}}>
              <th style={{padding:'8px 12px',color:'#fff',textAlign:'left'}}>Product</th>
              <th style={{padding:'8px 12px',color:'#fff',textAlign:'right'}}>Units</th>
              <th style={{padding:'8px 12px',color:'#fff',textAlign:'right'}}>Revenue</th>
            </tr></thead>
            <tbody>
              {items.map((item,i)=>(
                <tr key={i} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
                  <td style={{padding:'8px 12px',fontWeight:500,maxWidth:150,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{item.product_name}</td>
                  <td style={{padding:'8px 12px',textAlign:'right',color:'#8A8A8A'}}>{Number(item.total_qty).toLocaleString()}</td>
                  <td style={{padding:'8px 12px',textAlign:'right',fontWeight:700,color:'#A8893D'}}>{fmt(item.total_revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
